import React, { useEffect, useState } from 'react';
import { apiCall } from '../AuthContext';
import { Copy, CheckCircle2, Gift, Users, DollarSign, Award, Sparkles, Store, TrendingUp, Loader2, AlertCircle, Crown, ExternalLink } from 'lucide-react';

export default function Rewards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ payout_method: 'paypal', payout_details: '', audience: '', company_name: '' });
  const [status, setStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        apiCall('/api/rewards', { method: 'GET' }),
        apiCall('/api/referrals/me', { method: 'GET' }),
      ]);
      setData(r1.data);
      setReferralLink(r2.data.link);
      setReferralCode(r2.data.referral_code);
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Failed to load rewards' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const submitReseller = async () => {
    if (!form.payout_details.trim() || !form.audience.trim()) {
      setStatus({ kind: 'error', msg: 'Please fill payout details and audience' }); return;
    }
    setApplying(true); setStatus(null);
    try {
      await apiCall('/api/rewards/reseller-apply', { method: 'POST', data: form });
      setStatus({ kind: 'success', msg: 'Application submitted! We\'ll review within 2 business days.' });
      setShowApply(false);
      await load();
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Submission failed' });
    } finally { setApplying(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;
  if (!data) return null;

  const progressPct = data.next_tier ? Math.min(100, (data.paid_referrals / data.next_tier.threshold) * 100) : 100;
  const tierGranted = new Set((data.grants || []).map(g => g.tier_id));

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">Refer &amp; Earn</h1>
          <p className="text-slate-500 mt-1 text-base">Share your link, earn free months, unlock tier rewards, become a reseller.</p>
        </div>
        {data.is_vip && (
          <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-pink-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-md" data-testid="vip-badge">
            <Crown className="w-4 h-4" /> VIP
          </span>
        )}
      </div>

      {status && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border ${status.kind === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {status.kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {status.msg}
        </div>
      )}

      {/* Share card */}
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0052FF] rounded-2xl p-6 text-white" data-testid="share-card">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-cyan-300" />
          <span className="text-xs font-bold uppercase tracking-wider text-cyan-200">Your referral link</span>
        </div>
        <p className="text-lg font-semibold mb-1">Give 20% off · Get 1 free month</p>
        <p className="text-sm text-slate-300 mb-4">Your friend saves 20% on their first month. You get a full free month when they pay.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 bg-white/10 backdrop-blur rounded-lg border border-white/20 px-3 py-2.5 font-mono text-sm truncate" data-testid="referral-link-display">{referralLink}</div>
          <button onClick={copyLink} className="inline-flex items-center justify-center gap-2 bg-white text-[#0052FF] hover:bg-slate-100 font-semibold rounded-lg px-5 py-2.5 text-sm" data-testid="copy-referral-btn">
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-white/10">
          <div><p className="text-xs text-slate-400 uppercase tracking-wider">Clicks</p><p className="text-2xl font-bold">{data.total_clicks}</p></div>
          <div><p className="text-xs text-slate-400 uppercase tracking-wider">Signups</p><p className="text-2xl font-bold">{data.total_signups}</p></div>
          <div><p className="text-xs text-slate-400 uppercase tracking-wider">Paid Referrals</p><p className="text-2xl font-bold text-green-300">{data.paid_referrals}</p></div>
        </div>
      </div>

      {/* Tier progress */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="tier-progress-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2"><Award className="w-5 h-5 text-[#0052FF]" /><h2 className="text-lg font-bold text-[#0F172A]">Tier rewards</h2></div>
          {data.next_tier && (
            <span className="text-xs text-slate-500">
              <strong className="text-[#0F172A]">{data.next_tier.threshold - data.paid_referrals}</strong> more paid referrals until <strong className="text-[#0F172A]">{data.next_tier.title}</strong>
            </span>
          )}
        </div>
        {data.next_tier && (
          <div className="mb-5">
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0052FF] to-cyan-400 transition-all duration-700" style={{ width: `${progressPct}%` }} data-testid="tier-progress-bar" />
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          {data.tiers.map(t => {
            const unlocked = data.paid_referrals >= t.threshold;
            const claimed = tierGranted.has(t.id);
            return (
              <div key={t.id} className={`rounded-xl border p-4 transition-all ${unlocked ? 'border-green-300 bg-green-50/50' : 'border-slate-200 bg-white'}`} data-testid={`tier-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t.threshold} referrals</p>
                  {claimed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-bold">Claimed</span>}
                  {unlocked && !claimed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Unlocking...</span>}
                </div>
                <p className="text-base font-bold text-[#0F172A]">{t.title}</p>
                <p className="text-sm text-slate-600 mt-1">{t.reward_label}</p>
              </div>
            );
          })}
        </div>
        {data.available_credit_usd > 0 && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
            <DollarSign className="w-4 h-4" />
            <strong>${data.available_credit_usd.toFixed(2)}</strong> in rewards credit — auto-applied to your next paid upgrade.
          </div>
        )}
      </div>

      {/* Reseller program */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="reseller-card">
        <div className="flex items-center gap-2 mb-3">
          <Store className="w-5 h-5 text-[#0052FF]" />
          <h2 className="text-lg font-bold text-[#0F172A]">Reseller Program</h2>
          <span className="text-xs text-slate-500">· Earn {data.reseller_commission_pct}% recurring commission</span>
        </div>
        {data.reseller ? (
          <ResellerStatus reseller={data.reseller} />
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">Run a Shopify agency, POS install shop, or sell liquidation? Earn <strong>{data.reseller_commission_pct}% recurring</strong> commission on every subscription your referred customers pay — month after month. Auto-approved at <strong>{data.reseller_auto_promote_at}+ paid referrals</strong>.</p>
            {!showApply ? (
              <button onClick={() => setShowApply(true)} className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg px-5 py-2.5 text-sm" data-testid="apply-reseller-btn">
                <TrendingUp className="w-4 h-4" /> Apply as Reseller
              </button>
            ) : (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3" data-testid="reseller-apply-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Payout method</label>
                    <select value={form.payout_method} onChange={e => setForm(f => ({ ...f, payout_method: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="reseller-payout-method">
                      <option value="paypal">PayPal</option>
                      <option value="zelle">Zelle</option>
                      <option value="bank">Bank Transfer (US)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Payout details (email/phone/account)</label>
                    <input value={form.payout_details} onChange={e => setForm(f => ({ ...f, payout_details: e.target.value }))} placeholder="you@example.com" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="reseller-payout-details" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Company (optional)</label>
                  <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Acme Resellers LLC" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Who do you plan to refer?</label>
                  <textarea value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))} rows={3}
                    placeholder="E.g. Shopify agencies I work with, POS installer network, liquidation reseller Facebook groups..."
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="reseller-audience" />
                </div>
                <div className="flex gap-2">
                  <button onClick={submitReseller} disabled={applying} className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg px-5 py-2 text-sm disabled:opacity-50" data-testid="reseller-submit-btn">
                    {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Submit application
                  </button>
                  <button onClick={() => setShowApply(false)} className="inline-flex items-center px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Recent grants */}
      {data.grants && data.grants.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="grants-card">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#0052FF]" />
            <h2 className="text-lg font-bold text-[#0F172A]">Your rewards history</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {data.grants.map(g => (
              <div key={g.id} className="py-2.5 flex items-center justify-between text-sm" data-testid={`grant-${g.id}`}>
                <div>
                  <p className="font-semibold text-[#0F172A]">{g.tier_title}</p>
                  <p className="text-xs text-slate-500">{g.reward_label}</p>
                </div>
                <span className="text-xs text-slate-400">{new Date(g.granted_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResellerStatus({ reseller }) {
  const color = reseller.status === 'approved' ? 'green' : reseller.status === 'pending' ? 'amber' : 'red';
  return (
    <div className={`bg-${color}-50 border border-${color}-200 rounded-xl p-4`} data-testid={`reseller-status-${reseller.status}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-2 h-2 rounded-full bg-${color}-500`} />
        <p className={`text-sm font-bold text-${color}-800 uppercase tracking-wider`}>Reseller: {reseller.status}</p>
      </div>
      {reseller.status === 'pending' && <p className="text-xs text-amber-700 mt-1">We\u2019ll review your application within 2 business days. You\u2019ll receive an email once approved.</p>}
      {reseller.status === 'approved' && (
        <>
          <p className="text-sm text-green-800">You\u2019re an approved reseller earning <strong>{reseller.commission_pct}% recurring</strong> on every paid subscription of your referrals.</p>
          <p className="text-xs text-green-700 mt-1">Payout method: {reseller.payout_method || '—'} · {reseller.payout_details || ''}</p>
        </>
      )}
      {reseller.status === 'denied' && <p className="text-xs text-red-700 mt-1">Your application was not approved this time. Reach out to support for details.</p>}
    </div>
  );
}
